import clsx from 'clsx';
import { useLocation } from 'react-router-dom';

import { deviceNumberSizes } from 'config/device';
import { useNavigation } from 'hooks/useNavigation';
import { useWindowSize } from 'hooks/useWindowResize';

import { LinksWrapper } from './NavMenu.style';
import { NavListHeader } from '../NavBar.style';
import NavLinkLabel from '../NavLinkLabel';

type NavMenuLinkProps = {
  object: any;
  closeMenu: () => void;
};

function NavMenuLink({ object, closeMenu }: NavMenuLinkProps) {
  const { MENUOPTIONS, OBJECTNAME, ICON } = object;
  const navigate = useNavigation(object.OBJECTPATH);
  const { OBJECTPATH } = object;

  const location = useLocation();
  const { pathname } = location;

  const windowSize = useWindowSize();
  const isTabletDown =
    (windowSize.width ? windowSize.width : 0) <= deviceNumberSizes.tablet;

  function handleClick(option: any) {
    if (!option.PATH) return;
    if (isTabletDown) closeMenu();
    navigate(option);
  }

  function handleDirectClick() {
    // Find the LIST option (Λίστα) and navigate directly to it
    const listOption = MENUOPTIONS?.find((option: any) => option.TYPE === 'LIST');
    if (listOption) {
      if (isTabletDown) closeMenu();
      navigate(listOption);
    }
  }

  if (MENUOPTIONS?.length === 0) return null;

  // Check if we should show the smart menu
  const hasListOption = MENUOPTIONS?.some((option: any) => option.TYPE === 'LIST');

  // Smart menu logic: use LASTLEVELHIDE parameter or auto-detect
  const shouldUseSmartMenu =
    object.LASTLEVELHIDE !== undefined ? object.LASTLEVELHIDE : hasListOption; // Only requires LIST option, not CREATE

  // If smart menu is enabled, use direct navigation
  if (shouldUseSmartMenu && hasListOption) {
    const isActive = pathname.includes(OBJECTPATH);
    return (
      <li>
        <div
          data-cy={`${OBJECTNAME}-direct`}
          className={clsx('nav-link', {
            activeMenuItem: isActive,
          })}
          onClick={handleDirectClick}
          style={{
            padding: '0.5rem 0.5rem',
            cursor: 'pointer',
            borderRadius: '12px',
            transition: 'background-color 0.2s ease',
            backgroundColor: isActive ? undefined : 'transparent',
          }}
          onMouseEnter={(e) => {
            if (!isActive) {
              e.currentTarget.style.backgroundColor = '#f0f8ff';
            }
          }}
          onMouseLeave={(e) => {
            if (!isActive) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
        >
          <NavListHeader>
            {OBJECTNAME}
            {ICON}
          </NavListHeader>
        </div>
      </li>
    );
  }

  // Fallback to original submenu behavior
  return (
    <li>
      <NavListHeader style={{ padding: '0.5rem 0.5rem' }}>
        {OBJECTNAME}
        {ICON}
      </NavListHeader>
      <LinksWrapper>
        {MENUOPTIONS.map((option: any, index: number) => {
          if (option.HIDDEN) return null;
          const dynamicLink = formatDynamicLink(option, OBJECTPATH);
          return (
            <div key={index}>
              <div
                data-cy={`${OBJECTNAME}-${option.TITLE}`}
                className={clsx('nav-link', {
                  activeMenuItem: dynamicLink === decodeURIComponent(pathname),
                })}
                onClick={() => handleClick(option)}
              >
                <NavLinkLabel {...{ option, object }} />
              </div>
            </div>
          );
        })}
      </LinksWrapper>
    </li>
  );
}

function formatDynamicLink(option: any, objectPath: any) {
  let type = '';
  let itemUrl;

  if (option.TYPE === 'LIST') {
    type = 'search';
  }
  if (option.TYPE === 'CREATE') {
    type = 'new';
  }
  if (option.TYPE === 'CALENDAR') {
    type = 'calendar';
  }

  if (option.PATH && objectPath) {
    itemUrl = `/${objectPath}/${type}/${option.PATH}`;
  } else if (option.PATH) {
    itemUrl = `/${option.PATH}`;
  } else {
    itemUrl = `/${objectPath}/${type}`;
  }

  return itemUrl;
}

export default NavMenuLink;
